import React, { useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.js';
import Footer from '../components/Footer.jsx';
import { applyPageSeo } from '../utils/seo.js';
import { brandText } from '../components/BrandText.jsx';
import { withLinks } from '../utils/textLinks.jsx';
import { companyContact } from '../config/company.js';

const SectionHead = ({ title, subtitle, currentLang }) => (
  <header className="site-section-head">
    <h2>{brandText(title)}</h2>
    {subtitle && <p>{withLinks(subtitle, currentLang)}</p>}
  </header>
);

const QuantumPage = () => {
  const { currentLang, getPageTranslations } = useTranslation('quantum');
  const t = getPageTranslations('quantum');

  useEffect(() => {
    applyPageSeo({ ...t.seo, pageId: 'quantum' }, currentLang);
  }, [t.seo, currentLang]);

  const mailTo = (subject) => `mailto:${companyContact.email}?subject=${encodeURIComponent(subject || t.hero?.kicker || 'Quantum Computing & Quantum AI')}`;

  const hero = t.hero || {};
  const pillars = t.pillars || {};
  const gate = t.gate || {};
  const stack = t.stack || {};
  const cta = t.cta || {};

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      {/* Hero */}
      <section className="page-head pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div>
            {hero.kicker && <div className="editorial-kicker mb-4">{brandText(hero.kicker)}</div>}
            <h1 className="site-title">
              {brandText(hero.title)}
            </h1>
            {hero.claim && (
              <p className="site-lead">
                {withLinks(hero.claim, currentLang)}
              </p>
            )}
            {hero.subtitle && (
              <p className="site-copy">
                {withLinks(hero.subtitle, currentLang)}
              </p>
            )}
            {hero.cta && (
              <a href={mailTo(hero.kicker)} className="homepage-button-primary page-head__action inline-flex items-center justify-center gap-2">
                <Mail size={18} aria-hidden="true" />
                {hero.cta}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Four Capability Pillars */}
      {Array.isArray(pillars.items) && pillars.items.length > 0 && (
        <section className="homepage-section homepage-muted-panel">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHead title={pillars.title} subtitle={pillars.subtitle} currentLang={currentLang} />
            <div className="grid md:grid-cols-2 gap-5 items-stretch">
              {pillars.items.map((item, index) => (
                <article key={index} className="homepage-card p-6 md:p-7 h-full flex flex-col">
                  <h3 className="text-2xl font-black tracking-[-0.03em] text-text-primary mb-3">
                    {brandText(item.title)}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {withLinks(item.description, currentLang)}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Three-Stage Classical-First Validation Gate */}
      {Array.isArray(gate.stages) && gate.stages.length > 0 && (
        <section className="homepage-section">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHead title={gate.title} subtitle={gate.subtitle} currentLang={currentLang} />
            <div className="grid md:grid-cols-3 gap-5 items-stretch">
              {gate.stages.map((stage, index) => (
                <article key={index} className="homepage-card p-6 md:p-7 h-full flex flex-col">
                  {stage.step && (
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-primary-light mb-3">
                      {stage.step}
                    </p>
                  )}
                  <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-3">
                    {brandText(stage.title)}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {withLinks(stage.description, currentLang)}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Collaboration, AI Orchestration & Ecosystem */}
      {Array.isArray(stack.items) && stack.items.length > 0 && (
        <section className="homepage-section homepage-muted-panel">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHead title={stack.title} subtitle={stack.subtitle} currentLang={currentLang} />
            <div className="grid md:grid-cols-2 gap-5 items-stretch">
              {stack.items.map((item, index) => (
                <article key={index} className="homepage-card p-6 md:p-7 h-full flex flex-col">
                  <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-3">
                    {brandText(item.title)}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {withLinks(item.description, currentLang)}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Consultation CTA */}
      {cta.title && (
        <section className="pb-12 md:pb-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="site-contact">
              <div>
                <h2 className="site-heading">{brandText(cta.title)}</h2>
                {cta.subtitle && (
                  <p className="finance-cta__lead">
                    {withLinks(cta.subtitle, currentLang)}
                  </p>
                )}
              </div>
              {cta.button && (
                <a href={mailTo(cta.title)} className="homepage-button-primary">
                  {cta.button}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default QuantumPage;
